import appInfo from "@common/appInfo";
import Script from "@common/controls/Html/Script";
import Icon from "@common/controls/Icon/component";
import PreloaderDots from "@common/controls/Preloader/Dots/component";
import ResponsiveContainer from "@common/controls/ResponsiveContainer/component";
import { IconType } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { formatAspectRation } from "@common/lib/format";
import { uuid } from "@common/lib/misc";
import storage from "@common/lib/storage";
import classnames from "classnames";
import * as React from "react";
import { type VideoProps } from "../component";
import { DEFAULT_STORAGE_INFO, getAllow, type StorageInfo } from "../lib";
import { getDefaultColor, getSrc } from "./lib";

export type BunnyVideoProps = Omit<VideoProps, "posterUrl"> & {
  allowFullScreen?: boolean;
  color?: string;
  isAlwaysAvailable?: boolean;
  isProcessing?: boolean;
  libraryId?: string;
  videoId?: string;
};

const BunnyVideoInner = (props: BunnyVideoProps) => {
  const i18n = useI18n();

  const id = React.useMemo(() => `bunny_${uuid().replaceAll("-", "")}`, []);

  const color = React.useMemo(() => props.color || getDefaultColor(), [props.color]);
  const [isScriptLoaded, setIsScriptLoaded] = React.useState(false);

  const onScriptLoaded = React.useCallback(() => setIsScriptLoaded(true), []);

  const storageKey = React.useMemo(() => {
    if (!props.url && !props.videoId) throw new Error("BUG");

    let videoId = props.videoId;
    if (!videoId) {
      const i = props.url!.lastIndexOf("/");
      videoId = props.url!.substring(i + 1).match(/([^\?]+)/)![1];
    }

    return `bunny_${props.libraryId ?? appInfo.bunny?.libraryId}_${videoId}`.toLowerCase();
  }, [props.videoId, props.url]);

  React.useEffect(
    () => {
      if (!isScriptLoaded) return;

      let isCancelled = false;

      const onPause = () => storage.patch<StorageInfo>(storageKey, { isPaused: true }, DEFAULT_STORAGE_INFO);
      const onPlay = () => {
        storage.patch<StorageInfo>(storageKey, { isPaused: false }, DEFAULT_STORAGE_INFO);
        props.onStartPlay && props.onStartPlay();
      };
      const onReady = () => {
        const si = storage.get<StorageInfo>(storageKey);
        if (si?.seconds) {
          // TODO: figure out how to get rid off setTimeout
          setTimeout(() => {
            if (isCancelled) return;

            player.setCurrentTime(si.seconds);
            !si?.isPaused && player.play(); // will fail at least in chrome-based browsers
          }, 300);
        }
      };
      const onTimeupdate = (data: { seconds: number }) => {
        storage.patch<StorageInfo>(storageKey, { seconds: data.seconds }, DEFAULT_STORAGE_INFO);
      };

      const player = new window["playerjs"].Player(document.getElementById(id));
      player.on("ready", onReady);
      player.on("pause", onPause);
      player.on("play", onPlay);
      player.on("timeupdate", onTimeupdate);

      return () => {
        isCancelled = true;

        if (player) {
          try {
            player.off("ready", onReady);
            player.off("pause", onPause);
            player.off("play", onPlay);
            player.off("timeupdate", onTimeupdate);
          } catch {
            // unmount: https://github.com/embedly/player.js/issues/58
            // fails on unmount. Works on update
          }
        }
      };
    },
    [color, id, isScriptLoaded, storageKey, props.onStartPlay] // color is the key in the iframe
  );

  return (
    <ResponsiveContainer
      className={classnames("video_container", { background: props.isBackground })}
      aspectRatio={props.aspectRatio ? formatAspectRation(props.aspectRatio) : "16/9"}
    >
      {props.isProcessing ? (
        <div className="status">
          <Icon icon={IconType.Video} />
          <div className="text">
            <PreloaderDots text={i18n.processingVideo} />
          </div>
        </div>
      ) : (
        <div className="video bunny_video">
          {props.shouldTrack && (
            <Script src="//assets.mediadelivery.net/playerjs/player-0.1.0.min.js" isAsync onLoaded={onScriptLoaded} />
          )}

          <iframe
            key={color}
            id={id}
            allowFullScreen={props.allowFullScreen}
            allow={getAllow(props)}
            height="100%"
            width="100%"
            src={getSrc({
              url:
                props.url ||
                `https://iframe.mediadelivery.net/embed/${props.libraryId ?? appInfo.bunny?.libraryId}/${
                  props.videoId
                }`,
              color
            })}
          />

          {props.isBackground && <div className="overlay" />}
        </div>
      )}
    </ResponsiveContainer>
  );
};

const BunnyVideo = (props: BunnyVideoProps) => {
  // TODO: don’t use appInfo.isVideoUploadingEnabled here
  if (!appInfo.installedApps.includes("BunnyVideo") && !props.isAlwaysAvailable) return null;
  return <BunnyVideoInner {...props} />;
};
export default BunnyVideo;
